import { locales } from '~/helpers/locales'
import type { SearchHouse, SupportedCurrencies } from 'lc-services/types'

type CurrencyGroup = {
  code: SupportedCurrencies
  name: string
  symbol: string
}

export const MAX_DEFAULT_BUDGET = '1000000' as const
export const MIN_DEFAULT_BUDGET = '0' as const

export const currencies: CurrencyGroup[] = [
  {
    code: 'CHF',
    name: 'CHF',
    symbol: 'CHF',
  },
  {
    code: 'EUR',
    name: 'EUR - €',
    symbol: '€',
  },
  {
    code: 'USD',
    name: 'USD - $',
    symbol: '$',
  },
  {
    code: 'GBP',
    name: 'GBP - £',
    symbol: '£',
  },
]

const transformPrice = ({
  currency,
  hasDates,
  hit,
  i18n,
  isValidPeriod,
  userIsAdmin,
  userIsPartner,
}: {
  currency: SupportedCurrencies
  hasDates: boolean
  hit: any
  i18n: any
  isValidPeriod: boolean
  userIsAdmin: boolean
  userIsPartner: boolean
}) => {
  if (hasDates && isValidPeriod) {
    const eurPublicPrice: number | null = hit?.publicPrice || null
    return {
      ...periodPriceText(i18n, hit, userIsAdmin, userIsPartner),
      eurPublicPrice,
      isValid: true,
    }
  }

  const minPrice: number | null = hit?.min_week_price?.[currency] || null
  const maxPrice: number | null = hit?.max_week_price?.[currency] || null
  const text: string = weeklyPriceRangeText(
    i18n,
    minPrice,
    maxPrice,
    currency,
    hit.display_prices,
    userIsAdmin,
  )

  return {
    value: minPrice,
    text,
    isValid: false,
  }
}

const getPriceWithCurrency = ({
  price,
  currency,
  minimumFractionDigits = 0,
  i18n,
}: {
  price?: number | null
  currency: SupportedCurrencies
  minimumFractionDigits?: number
  i18n: { locale: MaybeRef<string> }
}) => {
  if (typeof price === 'undefined' || price === null) return ''
  const locale = unref(i18n.locale)
  const iso = locales.find((i) => i.code === locale)?.iso
  let options = {}

  if (currency) {
    options = {
      style: 'currency',
      currency,
    }
  }

  const formattedPrice = price.toLocaleString(iso, {
    ...options,
    minimumFractionDigits,
  })

  return formattedPrice.replace(/ /g, '\u00A0').replace(/\u202F/g, '\u00A0')
}

const periodPriceText = (
  i18n: { t: (...args: any[]) => string; locale: MaybeRef<string> },
  hit: {
    display_prices?: number
    publicPrice: number
    currency: SupportedCurrencies
    startAt: string
    endAt: string
  },
  userIsAdmin?: boolean,
  userIsPartner?: boolean,
) => {
  const currency = hit.currency
  let priceValue: number | null = null
  let priceText: string = i18n.t('house.price_upon_request')

  if (hit.display_prices || userIsAdmin) {
    priceValue = hit.publicPrice
    priceText = getPriceWithCurrency({
      price: priceValue,
      currency,
      i18n,
    })
  }

  if (priceValue) {
    const date1 = new Date(hit.startAt)
    const date2 = new Date(hit.endAt)
    const diff = date2.getTime() - date1.getTime()
    const nbNights = diff / (1000 * 3600 * 24)
    const nbNightsText = i18n.t('search.nbNights', nbNights)

    priceText = `${priceText} - ${nbNightsText}`
    if (userIsPartner) {
      priceText = `${i18n.t('search.from')} ${priceText}`
    }
  }

  return {
    value: priceValue,
    text: priceText,
  }
}

const weeklyPriceRangeText = (
  i18n: { t: (...args: any[]) => string; locale: MaybeRef<string> },
  min: number | null,
  max: number | null,
  currency: SupportedCurrencies,
  displayPrices: boolean,
  userIsAdmin = false,
) => {
  if (
    typeof min === 'number' &&
    typeof max === 'number' &&
    (displayPrices || userIsAdmin)
  ) {
    const minPrice = getPriceWithCurrency({ price: min, currency, i18n })
    const maxPrice = getPriceWithCurrency({ price: max, currency, i18n })

    if (min === max) {
      return i18n.t('global.pricePerWeek', {
        price: minPrice,
      })
    }

    return i18n.t('search.price_weekly_range', {
      min: minPrice,
      max: maxPrice,
    })
  }

  return i18n.t('house.price_upon_request')
}

const getPriceText = (
  i18n: { t: (...args: any[]) => string; locale: MaybeRef<string> },
  item: Pick<
    SearchHouse,
    'display_prices' | 'publicPrice' | 'min_week_price' | 'max_week_price'
  >,
  currency: SupportedCurrencies,
  period?: [string, string] | null,
) =>
  period?.length === 2 && item.publicPrice
    ? periodPriceText(
        i18n,
        {
          currency,
          endAt: period[1],
          startAt: period[0],
          publicPrice: item.publicPrice,
        },
        item.display_prices,
      ).text
    : weeklyPriceRangeText(
        i18n,
        item.min_week_price[currency],
        item.max_week_price[currency],
        currency,
        item.display_prices,
      )

const getCurrencySymbol = (code: SupportedCurrencies) => {
  return currencies.find((c) => c.code === code)?.symbol
}

const getOrdinal = (n: number, locale: string, suffixOnly = false) => {
  const suffixes: Record<number, string> = {
    1: locale === 'en' ? 'st' : 'er',
    2: locale === 'en' ? 'nd' : 'ème',
    3: locale === 'en' ? 'rd' : 'ème',
  }
  const suffix = locale === 'en' ? suffixes[n % 10] || 'th' : 'ème'

  return suffixOnly ? suffix : `${n}${suffix}`
}

const getMinMaxPrices = (
  items: Pick<SearchHouse, 'max_week_price' | 'min_week_price'>[],
  currency: MaybeRef<SupportedCurrencies>,
) => {
  if (!items.length) throw new Error('No prices found')
  const curr = unref(currency)
  const minPrice = Math.min(...items.map((item) => item.min_week_price[curr]))
  const maxPrice = Math.max(...items.map((item) => item.max_week_price[curr]))
  const delta = maxPrice - minPrice
  return [delta, minPrice, maxPrice] as const
}

const getPriceRanges = (
  prices: [number, number],
  { step, length }: { step: number; length: number },
) => {
  const [, max] = prices.map(ceilTo(step))

  return Array.from({ length }).reduce<number[][]>((acc, _, i) => {
    // insert a "below" range
    if (i === 0) return [[0, step]]
    const [prevMin, prevMax] = acc[acc.length - 1]
    // stop if the last range is the "above range"
    if (!prevMax || prevMin >= max) return acc
    // insert the "above" range
    // with max
    if (prevMax === max) return [...acc, [max]]
    // with previous range min value
    if (i === length - 1) return [...acc, [prevMin + step]]
    // add the "between" range
    return [...acc, [prevMax, prevMax + step]]
  }, [])
}

export {
  getCurrencySymbol,
  getMinMaxPrices,
  getPriceRanges,
  getPriceText,
  getPriceWithCurrency,
  getOrdinal,
  periodPriceText,
  transformPrice,
  weeklyPriceRangeText,
}
